/**
 * Billing: the friendly monthly note, not a dunning letter.
 *
 * Every line is a confirmed brief at the fare it was quoted at - `fareCents`,
 * the same frozen column the estimate and the pay sheet read. Two things are
 * said out loud rather than smoothed over, and both are deliberate:
 *
 *   - briefs confirmed but not yet priced, so the total is honest about what it
 *     does not include;
 *   - a brief history that could not be read at all, so a short list is not
 *     mistaken for a quiet month.
 *
 * @module web/screens/app/BillingScreen
 */
import { useEffect, useState } from "react";
import { get } from "../../api/client.ts";
import type { BillingSummary } from "../../api/types.ts";
import { fmtEur, fmtEurCompact } from "../../money.ts";
import { HistoricalBilling } from "./HistoricalBilling.tsx";

function billDate(ms: number): string {
  return new Date(ms).toLocaleDateString("en-IE", { day: "numeric", month: "short" });
}

export function BillingScreen() {
  const [failed, setFailed] = useState(false);
  const [summary, setSummary] = useState<BillingSummary | null>(null);

  useEffect(() => {
    let live = true;
    void get<BillingSummary>("/api/billing/summary")
      .then(d => { if (live) setSummary(d); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, []);

  return (
    <>
      <header className="page-head fade-in spend-page-head">
        <h1 className="t-h2">Billing</h1>
      </header>

      <HistoricalBilling />

      <div className="card fade-in spend-new" style={{ marginTop: 24 }}>
        <div className="t-eyebrow">{summary ? `New briefs · ${summary.periodLabel}` : "New briefs"}</div>
        <div className="t-display" style={{ margin: "10px 0 6px", fontSize: 44 }} id="billing-total">
          {summary ? fmtEur(summary.monthToDateCents) : "—"}
        </div>
        {summary?.buffer ? <div className="spend-threshold">
          <div className="spend-heading"><span>Payment threshold</span><strong>{fmtEurCompact(summary.buffer.thresholdCents)}</strong></div>
          <progress aria-label="New brief spend toward payment threshold" max={summary.buffer.thresholdCents} value={Math.min(summary.buffer.accruedCents, summary.buffer.thresholdCents)} />
          <div className="spend-caption">{fmtEur(summary.buffer.accruedCents)} accrued · {fmtEurCompact(summary.buffer.thresholdCents)} increments</div>
          <p className="spend-caption">Collected before your next brief once the threshold is reached.</p>
        </div> : <div className="t-body-sm">{summary ? "Charged as each brief is confirmed." : failed ? "Billing information is temporarily unavailable." : "Loading billing information…"}</div>}

        <div className="spend-new-method"><a className="btn btn-ghost" href="#billing/wallet">Payment method for new briefs</a></div>

        <div className="bill-tear" />

        <div className="tx-list">
          {!summary?.items?.length
            ? (
              <div className="t-body-sm" style={{ padding: "14px 0", color: "var(--ink-3)" }}>
                {summary?.briefsUnavailable ? "No items to show yet." : "New briefs will appear here."}
              </div>
            )
            : summary.items.map(i => (
              <div className="tx-row" key={i.briefId}>
                <div>
                  <div className="tx-desc">{i.title}</div>
                  <div className="tx-meta">Confirmed {billDate(i.billedAt)}</div>
                </div>
                <div className="tx-amount">{fmtEur(i.fareCents)}</div>
              </div>
            ))}
        </div>

        {(summary?.unpricedCount ?? 0) > 0 && (
          <div className="bill-aside">
            {summary!.unpricedCount === 1
              ? "1 more brief is being quoted by hand - it will appear here once priced."
              : `${summary!.unpricedCount} more briefs are being quoted by hand - they will appear here once priced.`}
          </div>
        )}

        {summary?.briefsUnavailable && (
          <div className="bill-aside">
            Your brief history is temporarily unavailable, so this list may be incomplete.
          </div>
        )}
      </div>

      {/* The balance, not the ledger. The full movement list lives one view
          across under "Balance"; rendering it twice was the reason four
          different places all looked like they were about money. Only shown
          when the wallet is actually in use - an empty card would imply this
          brand has one. */}
      {summary?.wallet && (
        <div style={{ maxWidth: 560 }}>
          <div className="rule" style={{ margin: "40px 0 18px" }}>Balance</div>
          <div className="card bill-note-card">
            <div className="t-eyebrow">Balance</div>
            <div className="t-h2" style={{ margin: "8px 0 0" }}>{fmtEurCompact(summary.wallet.balanceCents)}</div>
            <p className="t-body-sm" style={{ marginTop: 10, color: "var(--ink-3)" }}>
              Every movement is listed under <strong>Balance</strong>.
            </p>
          </div>
        </div>
      )}
    </>
  );
}
